
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import moment from 'moment-timezone';

// Timezone pro zobrazování - vždy Praha bez ohledu na TZ prohlížeče
const DISPLAY_TIMEZONE = 'Europe/Prague';

import DatePicker from '@/libs/common/components/DatePicker.vue';
import LocationSelect from '@/libs/common/components-smart/LocationSelect.vue';
import WorkplaceSelect from '@/libs/common/components-smart/WorkplaceSelect.vue';
import ProjectSelect from '@/libs/common/components-smart/ProjectSelect.vue';
import ShiftTypeSelect from '@/libs/common/components-smart/ShiftTypeSelect.vue';
import ShiftTeamTypeSelect from '@/libs/common/components-smart/ShiftTeamTypeSelect.vue';
import FaultTypeSelect from '@/libs/common/components-smart/FaultTypeSelect.vue';
import {
    toProductionDayStart,
    toProductionDayEnd,
    getLastCompletedProductionDayEnd,
    getDayStartMinutes,
} from '@/libs/tpm-analytics/core/production-day.utils';
import { getPreviousDateRange, getDefaultGranularity, getAllowedGranularities } from '@/libs/tpm-analytics/core/date-range.utils';

// Ujistíme se, že moment.js používá týden začínající pondělím (ISO standard)
moment.updateLocale('cs', {
    week: {
        dow: 1, // Pondělí je první den v týdnu (0 = neděle, 1 = pondělí)
        doy: 4, // První týden roku je týden obsahující 4. leden (ISO standard)
    },
});

@Component({
    components: {
        DatePicker,
        LocationSelect,
        WorkplaceSelect,
        ProjectSelect,
        ShiftTypeSelect,
        ShiftTeamTypeSelect,
        FaultTypeSelect,
    },
})
export default class TpmFiltersBar extends Vue {
    @Prop({ required: true }) readonly value!: any;
    @Prop({ default: false }) readonly showFaultType!: boolean;

    local: any = {
        periodPreset: 'last7d',
        customFrom: null,
        customTo: null,
        granularity: 'day' as 'day' | 'week' | 'month',
        selectedLocationIds: [] as string[],
        selectedWorkplaceIds: [] as string[],
        selectedProjectIds: [] as string[],
        selectedShiftTypeIds: [] as string[],
        selectedShiftTeamTypeIds: [] as string[],
        selectedFaultTypeIds: [] as string[],
    };

    // Guard pro zabránění automatické validace po manuální změně granularity
    private manualGranularityChange = false;

    created() {
        this.copyFrom(this.value);
    }

    @Watch('value', { deep: true })
    onValueChanged(v: any) {
        this.copyFrom(v);
    }

    copyFrom(v: any) {
        const src = v || {};
        this.local = {
            periodPreset: src.periodPreset || 'last7d',
            customFrom: src.customFrom || null,
            customTo: src.customTo || null,
            granularity: src.granularity || 'day',
            selectedLocationIds: Array.isArray(src.selectedLocationIds) ? src.selectedLocationIds : [],
            selectedWorkplaceIds: Array.isArray(src.selectedWorkplaceIds) ? src.selectedWorkplaceIds : [],
            selectedProjectIds: Array.isArray(src.selectedProjectIds) ? src.selectedProjectIds : [],
            selectedShiftTypeIds: Array.isArray(src.selectedShiftTypeIds) ? src.selectedShiftTypeIds : [],
            selectedShiftTeamTypeIds: Array.isArray(src.selectedShiftTeamTypeIds) ? src.selectedShiftTeamTypeIds : [],
            selectedFaultTypeIds: Array.isArray(src.selectedFaultTypeIds) ? src.selectedFaultTypeIds : [],
        } as any;
    }

    get periodItems() {
        return [
            { text: this.$t('tpmFilters.previousDay') as any, value: 'previousDay' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.last7d') as any, value: 'last7d' },
            { text: this.$t('tpmFilters.last14d') as any, value: 'last14d' },
            { text: this.$t('tpmFilters.last30d') as any, value: 'last30d' },
            { text: this.$t('tpmFilters.last3m') as any, value: 'last3m' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.thisWeek') as any, value: 'thisWeek' },
            { text: this.$t('tpmFilters.lastWeek') as any, value: 'lastWeek' },
            { text: this.$t('tpmFilters.thisMonth') as any, value: 'thisMonth' },
            { text: this.$t('tpmFilters.lastMonth') as any, value: 'lastMonth' },
            { text: this.$t('tpmFilters.thisQuarter') as any, value: 'thisQuarter' },
            { text: this.$t('tpmFilters.lastQuarter') as any, value: 'lastQuarter' },
            { text: this.$t('tpmFilters.thisYear') as any, value: 'thisYear' },
            { text: this.$t('tpmFilters.lastYear') as any, value: 'lastYear' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.presetCustom') as any, value: 'custom' },
        ];
    }

    get allowedGranularityItems() {
        // Získáme povolené granularity pro aktuální období
        const allowed = getAllowedGranularities({
            periodPreset: this.local.periodPreset,
            customFrom: this.local.customFrom,
            customTo: this.local.customTo,
        });

        // Všechny možné granularity
        const allItems = [
            { text: this.$t('tpmAnalytics.granularityDay') as any, value: 'day' },
            { text: this.$t('tpmAnalytics.granularityWeek') as any, value: 'week' },
            { text: this.$t('tpmAnalytics.granularityMonth') as any, value: 'month' },
        ];

        // Vrátíme pouze povolené
        return allItems.filter((item) => allowed.includes(item.value as any));
    }

    getRange() {
        console.log('[TpmFiltersBar] getRange() - START', {
            periodPreset: this.local.periodPreset,
            granularity: this.local.granularity,
            customFrom: this.local.customFrom,
            customTo: this.local.customTo,
        });

        // Konec období je vždy začátek dnešního výrobního dne (= konec včerejšího výrobního dne)
        const lastCompletedDayEnd = getLastCompletedProductionDayEnd();
        console.log('[TpmFiltersBar] getRange() - lastCompletedDayEnd:', lastCompletedDayEnd);

        let to = lastCompletedDayEnd;
        let from = moment(to).subtract(7, 'days').toISOString();
        const p = this.local.periodPreset;

        // Předchozí den - celý včerejší výrobní den
        if (p === 'previousDay') {
            from = moment(lastCompletedDayEnd).subtract(1, 'day').toISOString();
            to = lastCompletedDayEnd;
            console.log('[TpmFiltersBar] getRange() - previousDay:', { from, to });
        }
        // Posledních X dní - vždy od (dnes - X dní) do konce včerejšího výrobního dne
        else if (p === 'last7d') {
            from = moment(lastCompletedDayEnd).subtract(7, 'days').toISOString();
            to = lastCompletedDayEnd;
            console.log('[TpmFiltersBar] getRange() - last7d:', { from, to });
        }
        else if (p === 'last14d') {
            from = moment(lastCompletedDayEnd).subtract(14, 'days').toISOString();
            to = lastCompletedDayEnd;
            console.log('[TpmFiltersBar] getRange() - last14d:', { from, to });
        }
        else if (p === 'last30d') {
            from = moment(lastCompletedDayEnd).subtract(30, 'days').toISOString();
            to = lastCompletedDayEnd;
            console.log('[TpmFiltersBar] getRange() - last30d:', { from, to });
        }
        else if (p === 'last3m') {
            from = moment(lastCompletedDayEnd).subtract(3, 'months').toISOString();
            to = lastCompletedDayEnd;
            console.log('[TpmFiltersBar] getRange() - last3m:', { from, to });
        }
        // Tento týden - od pondělí do konce včerejšího výrobního dne
        else if (p === 'thisWeek') {
            // DŮLEŽITÉ: Nastavíme from na poledne (12:00), aby toProductionDayStart() neposunul datum o den zpět
            from = moment(lastCompletedDayEnd).startOf('isoWeek').hour(12).minute(0).second(0).millisecond(0).toISOString();
            to = lastCompletedDayEnd;
            console.log('[TpmFiltersBar] getRange() - thisWeek:', { 
                from, 
                to,
                fromDay: moment(from).format('dddd DD.MM.YYYY HH:mm'),
                toDay: moment(to).format('dddd DD.MM.YYYY HH:mm'),
            });
        }
        // Minulý týden - od pondělí do neděle předchozího týdne
        else if (p === 'lastWeek') {
            const lastMonday = moment(lastCompletedDayEnd).startOf('isoWeek').subtract(7, 'days');
            // DŮLEŽITÉ: Nastavíme from na poledne (12:00)
            from = lastMonday.hour(12).minute(0).second(0).millisecond(0).toISOString();
            // Konec týdne = neděle 23:59:59.999
            to = lastMonday.clone().endOf('isoWeek').toISOString();
            console.log('[TpmFiltersBar] getRange() - lastWeek:', { from, to });
        }
        // Tento měsíc - od 1. dne měsíce do konce včerejšího výrobního dne
        else if (p === 'thisMonth') {
            // DŮLEŽITÉ: Nastavíme from na poledne (12:00)
            from = moment(lastCompletedDayEnd).startOf('month').hour(12).minute(0).second(0).millisecond(0).toISOString();
            to = lastCompletedDayEnd;
            console.log('[TpmFiltersBar] getRange() - thisMonth:', { from, to });
        }
        // Minulý měsíc - od 1. dne do posledního dne předchozího měsíce
        else if (p === 'lastMonth') {
            const lastMonthStart = moment(lastCompletedDayEnd).subtract(1, 'month').startOf('month');
            // DŮLEŽITÉ: Nastavíme from na poledne (12:00)
            from = lastMonthStart.hour(12).minute(0).second(0).millisecond(0).toISOString();
            // Konec měsíce = poslední den 23:59:59.999
            to = lastMonthStart.clone().endOf('month').toISOString();
            console.log('[TpmFiltersBar] getRange() - lastMonth:', { from, to });
        }
        // Toto čtvrtletí - od 1. dne čtvrtletí do konce včerejšího výrobního dne
        else if (p === 'thisQuarter') {
            // DŮLEŽITÉ: Nastavíme from na poledne (12:00)
            from = moment(lastCompletedDayEnd).startOf('quarter').hour(12).minute(0).second(0).millisecond(0).toISOString();
            to = lastCompletedDayEnd;
            console.log('[TpmFiltersBar] getRange() - thisQuarter:', { from, to });
        }
        // Minulé čtvrtletí - od 1. dne do posledního dne předchozího čtvrtletí
        else if (p === 'lastQuarter') {
            const lastQuarterStart = moment(lastCompletedDayEnd).subtract(1, 'quarter').startOf('quarter');
            // DŮLEŽITÉ: Nastavíme from na poledne (12:00)
            from = lastQuarterStart.hour(12).minute(0).second(0).millisecond(0).toISOString();
            // Konec čtvrtletí = poslední den 23:59:59.999
            to = lastQuarterStart.clone().endOf('quarter').toISOString();
            console.log('[TpmFiltersBar] getRange() - lastQuarter:', { from, to });
        }
        // Tento rok - od 1.1. do konce včerejšího výrobního dne
        else if (p === 'thisYear') {
            // DŮLEŽITÉ: Nastavíme from na poledne (12:00)
            from = moment(lastCompletedDayEnd).startOf('year').hour(12).minute(0).second(0).millisecond(0).toISOString();
            to = lastCompletedDayEnd;
            console.log('[TpmFiltersBar] getRange() - thisYear:', { from, to });
        }
        // Minulý rok - od 1.1. do 31.12. předchozího roku
        else if (p === 'lastYear') {
            const lastYearStart = moment(lastCompletedDayEnd).subtract(1, 'year').startOf('year');
            // DŮLEŽITÉ: Nastavíme from na poledne (12:00)
            from = lastYearStart.hour(12).minute(0).second(0).millisecond(0).toISOString();
            // Konec roku = 31.12. 23:59:59.999
            to = lastYearStart.clone().endOf('year').toISOString();
            console.log('[TpmFiltersBar] getRange() - lastYear:', { from, to });
        }
        // Custom rozsah od uživatele
        else if (p === 'custom' && this.local.customFrom && this.local.customTo) {
            from = this.local.customFrom;
            to = this.local.customTo;
            console.log('[TpmFiltersBar] getRange() - custom:', { from, to });
        }

        console.log('[TpmFiltersBar] getRange() - PŘED převedením na výrobní dny:', { from, to });

        // DŮLEŽITÉ: Zajistit, že 'to' není v budoucnu (později než začátek dnešního výrobního dne)
        const maxAllowedEnd = lastCompletedDayEnd;
        if (moment(to).isAfter(maxAllowedEnd)) {
            console.log('[TpmFiltersBar] getRange() - to je v budoucnu, ořezávám:', {
                before: to,
                after: maxAllowedEnd,
            });
            to = maxAllowedEnd;
        }

        console.log('[TpmFiltersBar] getRange() - PŘED převedením na výrobní dny:', { from, to });

        // Převedeme všechny rozsahy na výrobní dny
        // Pro CUSTOM období: pouze přidáme dayStart čas, NESMÍME měnit datum!
        // Pro ostatní období: standardní převod pomocí toProductionDayStart/End
        const beforeProdFrom = from;
        const beforeProdTo = to;
        
        if (p === 'custom') {
            // Pro custom: nastavíme jen čas podle dayStart, datum zůstává stejné
            const dayStartMinutes = getDayStartMinutes();
            const dayStartHours = Math.floor(dayStartMinutes / 60);
            const dayStartMins = dayStartMinutes % 60;
            
            // from: nastavit čas na dayStart, datum zachovat
            const fromMoment = moment(from);
            from = fromMoment.hour(dayStartHours).minute(dayStartMins).second(0).millisecond(0).toISOString();
            
            // to: nastavit čas na dayStart, datum zachovat
            const toMoment = moment(to);
            to = toMoment.hour(dayStartHours).minute(dayStartMins).second(0).millisecond(0).toISOString();
        } else {
            // Standardní převod pro preset období
            from = toProductionDayStart(from);
            to = toProductionDayEnd(to);
        }

        console.log('[TpmFiltersBar] getRange() - PO převedení na výrobní dny:', {
            beforeFrom: beforeProdFrom,
            afterFrom: from,
            beforeTo: beforeProdTo,
            afterTo: to,
            fromDay: moment(from).format('dddd DD.MM.YYYY HH:mm'),
            toDay: moment(to).format('dddd DD.MM.YYYY HH:mm'),
        });
        
        // Finální kontrola - zajistit, že to není v budoucnu po převodu na výrobní den
        if (moment(to).isAfter(maxAllowedEnd)) {
            console.log('[TpmFiltersBar] getRange() - FINÁLNÍ kontrola: to je v budoucnu, ořezávám:', {
                before: to,
                after: maxAllowedEnd,
            });
            to = maxAllowedEnd;
        }

        console.log('[TpmFiltersBar] getRange() - RESULT:', {
            from,
            to,
            fromDay: moment.tz(from, DISPLAY_TIMEZONE).format('dddd DD.MM.YYYY HH:mm'),
            toDay: moment.tz(to, DISPLAY_TIMEZONE).format('dddd DD.MM.YYYY HH:mm'),
        });

        return { from, to };
    }

    get currentPeriodText() {
        const { from, to } = this.getRange();
        const fmt = (v: string) => this.firstLetterUppercase(moment.tz(v, DISPLAY_TIMEZONE).format('ddd DD.MM.YYYY HH:mm'));
        return `${fmt(from)} – ${fmt(to)}`;
    }
    get previousPeriodText() {
        const { from: prevFrom, to: prevTo } = getPreviousDateRange({
            periodPreset: this.local.periodPreset,
            customFrom: this.local.customFrom,
            customTo: this.local.customTo,
            granularity: this.local.granularity,
        });
        const fmt = (v: string) => this.firstLetterUppercase(moment.tz(v, DISPLAY_TIMEZONE).format('ddd DD.MM.YYYY HH:mm'));
        return `${fmt(prevFrom)} – ${fmt(prevTo)}`;
    }

    @Watch('local.granularity')
    onGranularityWatchTriggered(newVal: any, oldVal: any) {
        console.log('[TpmFiltersBar] WATCH local.granularity triggered', {
            oldValue: oldVal,
            newValue: newVal,
            manualGranularityChange: this.manualGranularityChange,
            stackTrace: new Error().stack,
        });
    }

    @Watch('local.periodPreset')
    @Watch('local.customFrom')
    @Watch('local.customTo')
    onPeriodChanged() {
        console.log('[TpmFiltersBar] onPeriodChanged called', {
            manualGranularityChange: this.manualGranularityChange,
            periodPreset: this.local.periodPreset,
            currentGranularity: this.local.granularity,
        });

        // Pokud byla granularita změněna manuálně, NEvykonáváme validaci
        // Flag zůstane true a resetuje se až v nextTick v onGranularityChanged
        if (this.manualGranularityChange) {
            console.log('[TpmFiltersBar] onPeriodChanged SKIPPED - manual granularity change');
            return;
        }

        // Zkontrolovat, zda je aktuální granularita povolená pro nové období
        const allowed = getAllowedGranularities({
            periodPreset: this.local.periodPreset,
            customFrom: this.local.customFrom,
            customTo: this.local.customTo,
        });
        
        const currentIsAllowed = allowed.includes(this.local.granularity);
        
        if (!currentIsAllowed) {
            // Aktuální granularita není povolená → nastavit default
            const defaultGranularity = getDefaultGranularity({
                periodPreset: this.local.periodPreset,
                customFrom: this.local.customFrom,
                customTo: this.local.customTo,
            });
            
            console.log('[TpmFiltersBar] onPeriodChanged - current granularity not allowed, setting default', {
                from: this.local.granularity,
                to: defaultGranularity,
                allowed,
            });
            
            this.local.granularity = defaultGranularity;
        } else {
            console.log('[TpmFiltersBar] onPeriodChanged - current granularity is allowed, keeping it', {
                granularity: this.local.granularity,
                allowed,
            });
        }

        // Vždy emitujeme změnu po validaci
        this.emitState();
    }

    emitState() {
        console.log('[TpmFiltersBar] emitState called', {
            periodPreset: this.local.periodPreset,
            granularity: this.local.granularity,
            manualGranularityChange: this.manualGranularityChange,
        });
        const copy = { ...(this.local || {}) } as any;
        this.$emit('input', copy);
        this.$emit('change');
    }

    onGranularityChanged() {
        console.log('[TpmFiltersBar] onGranularityChanged called', {
            newGranularity: this.local.granularity,
            manualGranularityChange: this.manualGranularityChange,
        });
        
        // Nastavíme guard flag při manuální změně granularity
        this.manualGranularityChange = true;
        console.log('[TpmFiltersBar] manualGranularityChange set to TRUE');
        
        // Resetujeme flag až po dokončení všech synchronních operací (včetně watcherů)
        this.$nextTick(() => {
            console.log('[TpmFiltersBar] nextTick - resetting manualGranularityChange to FALSE');
            this.manualGranularityChange = false;
        });
        
        this.emitState();
    }

    firstLetterUppercase(value: string) {
        return value.substring(0, 1).toUpperCase() + value.substring(1);
    }
}
